import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { DashboardLayout, adminNavItems } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { 
  Search, 
  Plus, 
  MapPin, 
  Users,
  FileText,
  Edit,
  Eye,
  Trash2,
  ClipboardList
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyProject = {
  title: "",
  description: "",
  location_address: "",
  location_city: "",
  project_type: "assembly",
  start_date: "",
  end_date: "",
  required_crew_size: 2,
  complexity: "medium",
  estimated_duration_hours: "",
  safety_notes: "",
  scope_details: "",
  budget_min: "",
  budget_max: "",
  is_public: true,
};

export default function AdminProjects() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Quote dialog
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [quoteData, setQuoteData] = useState({
    proposed_price: "",
    proposed_start_date: "",
    terms: "Standard service terms apply. Net 14 payment.",
  });

  // Project dialog (create/edit)
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectData, setProjectData] = useState(emptyProject);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQuote = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/quotes`, {
        project_id: selectedProject.project_id,
        proposed_price: parseFloat(quoteData.proposed_price),
        proposed_start_date: quoteData.proposed_start_date,
        terms: quoteData.terms,
      });
      toast.success(t('success.created'));
      setQuoteDialogOpen(false);
      setQuoteData({ proposed_price: "", proposed_start_date: "", terms: "Standard service terms apply. Net 14 payment." });
      fetchProjects();
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const openCreateProject = () => {
    setEditingProject(null);
    setProjectData(emptyProject);
    setProjectDialogOpen(true);
  };

  const openEditProject = (project) => {
    setEditingProject(project);
    setProjectData({
      title: project.title || "",
      description: project.description || "",
      location_address: project.location_address || "",
      location_city: project.location_city || "",
      project_type: project.project_type || "assembly",
      start_date: project.start_date || "",
      end_date: project.end_date || "",
      required_crew_size: project.required_crew_size || 2,
      complexity: project.complexity || "medium",
      estimated_duration_hours: project.estimated_duration_hours || "",
      safety_notes: project.safety_notes || "",
      scope_details: project.scope_details || "",
      budget_min: project.budget_min || "",
      budget_max: project.budget_max || "",
      is_public: project.is_public !== false,
    });
    setProjectDialogOpen(true);
  };

  const handleSaveProject = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        ...projectData,
        required_crew_size: parseInt(projectData.required_crew_size) || 2,
        estimated_duration_hours: projectData.estimated_duration_hours 
          ? parseFloat(projectData.estimated_duration_hours) 
          : null,
        budget_min: projectData.budget_min ? parseFloat(projectData.budget_min) : null,
        budget_max: projectData.budget_max ? parseFloat(projectData.budget_max) : null,
      };

      if (editingProject) {
        // Update existing project
        await axios.put(`${API}/projects/${editingProject.project_id}`, data);
        toast.success(t('success.updated'));
      } else {
        // Create new project
        const response = await axios.post(`${API}/projects`, data);
        // Auto-post the project so it appears in public browse
        await axios.put(`${API}/projects/${response.data.project_id}/status?status=posted`);
        toast.success(t('success.created'));
      }

      setProjectDialogOpen(false);
      setProjectData(emptyProject);
      setEditingProject(null);
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save project");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStatus = async (projectId, newStatus) => {
    try {
      await axios.put(`${API}/projects/${projectId}/status?status=${newStatus}`);
      toast.success(`Status updated to ${newStatus}`);
      fetchProjects();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const filteredProjects = projects.filter(
    (project) =>
      project.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.location_city?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const statusColors = {
    draft: "bg-gray-500/20 text-gray-400",
    posted: "bg-blue-500/20 text-blue-400",
    quoted: "bg-purple-500/20 text-purple-400",
    contracted: "bg-indigo-500/20 text-indigo-400",
    scheduled: "bg-yellow-500/20 text-yellow-400",
    in_progress: "bg-orange-500/20 text-orange-400",
    completed: "bg-green-500/20 text-green-400",
    invoiced: "bg-teal-500/20 text-teal-400",
    paid: "bg-emerald-500/20 text-emerald-400",
  };

  const statusOptions = [
    "draft", "posted", "quoted", "contracted", "scheduled", 
    "in_progress", "completed", "invoiced", "paid"
  ];

  return (
    <DashboardLayout navItems={adminNavItems} title={t('nav.projects')}>
      <div className="space-y-6" data-testid="admin-projects">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex-1 w-full sm:max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#475569]" />
              <Input
                type="text"
                placeholder={t('admin.searchProjects')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-11 bg-black/50 border-white/10 text-white placeholder:text-[#475569] focus:border-[#23F7DD]"
              />
            </div>
          </div>

          <Button 
            onClick={openCreateProject}
            className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold"
            data-testid="add-project-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            {t('admin.addProject')}
          </Button>
        </div>

        {/* Projects Table */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : filteredProjects.length === 0 ? (
              <div className="text-center py-12">
                <Search className="w-12 h-12 text-[#475569] mx-auto mb-4" />
                <p className="text-[#94A3B8]">{t('admin.noProjectsFound')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-4 text-xs uppercase text-[#94A3B8] font-medium">{t('projects.title')}</th>
                      <th className="text-left p-4 text-xs uppercase text-[#94A3B8] font-medium">{t('admin.location')}</th>
                      <th className="text-left p-4 text-xs uppercase text-[#94A3B8] font-medium">{t('admin.budget')}</th>
                      <th className="text-left p-4 text-xs uppercase text-[#94A3B8] font-medium">{t('admin.crew')}</th>
                      <th className="text-left p-4 text-xs uppercase text-[#94A3B8] font-medium">{t('common.status')}</th>
                      <th className="text-left p-4 text-xs uppercase text-[#94A3B8] font-medium">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProjects.map((project) => (
                      <tr key={project.project_id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="p-4">
                          <div>
                            <p className="text-white font-medium">{project.title}</p>
                            <p className="text-sm text-[#94A3B8]">{project.project_type} • {project.start_date || "TBD"}</p>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2 text-[#94A3B8]">
                            <MapPin className="w-4 h-4" />
                            {project.location_city}
                          </div>
                        </td>
                        <td className="p-4">
                          {project.budget_min || project.budget_max ? (
                            <span className="text-[#23F7DD] font-medium">
                              {project.budget_min?.toLocaleString() || "?"} - {project.budget_max?.toLocaleString() || "?"} NOK
                            </span>
                          ) : (
                            <span className="text-[#94A3B8]">{t('admin.notSet')}</span>
                          )}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2 text-[#94A3B8]">
                            <Users className="w-4 h-4" />
                            {project.required_crew_size}
                          </div>
                        </td>
                        <td className="p-4">
                          <select
                            value={project.status}
                            onChange={(e) => handleUpdateStatus(project.project_id, e.target.value)}
                            className={`px-3 py-1 rounded-full text-xs font-medium border-0 cursor-pointer ${statusColors[project.status] || "bg-gray-500/20 text-gray-400"}`}
                          >
                            {statusOptions.map((status) => (
                              <option key={status} value={status} className="bg-[#0A0A0A] text-white">
                                {status.replace("_", " ")}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditProject(project)}
                              className="text-[#94A3B8] hover:text-white hover:bg-white/10"
                              data-testid={`edit-project-${project.project_id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>

                            <Button
                              size="sm"
                              onClick={() => navigate(`/admin/projects/${project.project_id}/applications`)}
                              className="bg-blue-500 text-white hover:bg-blue-600"
                              data-testid={`view-applications-${project.project_id}`}
                            >
                              <ClipboardList className="w-4 h-4 mr-1" />
                              {t('admin.applications')}
                            </Button>

                            <Button
                              size="sm"
                              onClick={() => navigate(`/admin/projects/${project.project_id}/detail`)}
                              className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
                              data-testid={`go-to-details-${project.project_id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              {t('admin.projectDetails')}
                            </Button>

                            {project.status === "posted" && (
                              <Dialog open={quoteDialogOpen && selectedProject?.project_id === project.project_id} onOpenChange={(open) => {
                                setQuoteDialogOpen(open);
                                if (open) setSelectedProject(project);
                              }}>
                                <DialogTrigger asChild>
                                  <Button
                                    size="sm"
                                    className="bg-purple-500 text-white hover:bg-purple-600"
                                    data-testid={`create-quote-${project.project_id}`}
                                  >
                                    <FileText className="w-4 h-4 mr-1" />
                                    Quote
                                  </Button>
                                </DialogTrigger>
                                <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
                                  <DialogHeader>
                                    <DialogTitle className="font-['Unbounded']">{t('admin.createQuote')}</DialogTitle>
                                  </DialogHeader>
                                  <form onSubmit={handleCreateQuote} className="space-y-4">
                                    <div className="p-3 bg-white/5 rounded-lg">
                                      <p className="text-sm text-[#94A3B8]">{t('projects.title')}</p>
                                      <p className="text-white font-medium">{project.title}</p>
                                      {(project.budget_min || project.budget_max) && (
                                        <p className="text-sm text-[#23F7DD] mt-1">
                                          {t('admin.clientBudget')}: {project.budget_min?.toLocaleString()} - {project.budget_max?.toLocaleString()} NOK
                                        </p>
                                      )}
                                    </div>
                                    
                                    <div className="space-y-2">
                                      <Label className="text-[#94A3B8]">{t('admin.yourQuotePrice')}</Label>
                                      <Input
                                        type="number"
                                        value={quoteData.proposed_price}
                                        onChange={(e) => setQuoteData({ ...quoteData, proposed_price: e.target.value })}
                                        placeholder="50000"
                                        className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                                        required
                                      />
                                    </div>

                                    <div className="space-y-2">
                                      <Label className="text-[#94A3B8]">{t('admin.proposedStartDate')}</Label>
                                      <Input
                                        type="date"
                                        value={quoteData.proposed_start_date}
                                        onChange={(e) => setQuoteData({ ...quoteData, proposed_start_date: e.target.value })}
                                        className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                                      />
                                    </div>

                                    <div className="flex justify-end gap-3 pt-4">
                                      <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setQuoteDialogOpen(false)}
                                        className="border-white/20 text-[#94A3B8]"
                                      >
                                        {t('common.cancel')}
                                      </Button>
                                      <Button type="submit" className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]">
                                        {t('admin.sendQuote')}
                                      </Button>
                                    </div>
                                  </form>
                                </DialogContent>
                              </Dialog>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Project Dialog */}
        <Dialog open={projectDialogOpen} onOpenChange={setProjectDialogOpen}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Unbounded']">
                {editingProject ? t('admin.editProject') : t('admin.createNewProject')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSaveProject} className="space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[#94A3B8]">{t('admin.title')} *</Label>
                  <Input
                    value={projectData.title}
                    onChange={(e) => setProjectData({ ...projectData, title: e.target.value })}
                    placeholder={t('admin.projectTitle')}
                    className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-[#94A3B8]">{t('common.description')}</Label>
                  <Textarea
                    value={projectData.description}
                    onChange={(e) => setProjectData({ ...projectData, description: e.target.value })}
                    placeholder={t('admin.projectDescription')}
                    rows={3}
                    className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                  />
                </div>
              </div>

              {/* Location */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#94A3B8]">{t('common.address')} *</Label>
                  <Input
                    value={projectData.location_address}
                    onChange={(e) => setProjectData({ ...projectData, location_address: e.target.value })}
                    placeholder={t('admin.streetAddress')}
                    className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#94A3B8]">{t('common.city')} *</Label>
                  <Input
                    value={projectData.location_city}
                    onChange={(e) => setProjectData({ ...projectData, location_city: e.target.value })}
                    placeholder="Oslo"
                    className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                    required
                  />
                </div>
              </div>

              {/* Type & Schedule */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#94A3B8]">{t('admin.type')}</Label>
                  <select
                    value={projectData.project_type}
                    onChange={(e) => setProjectData({ ...projectData, project_type: e.target.value })}
                    className="w-full bg-black/50 border border-white/10 text-white px-4 py-2 rounded-lg focus:border-[#23F7DD]"
                  >
                    <option value="assembly">{t('admin.assembly')}</option>
                    <option value="disassembly">{t('admin.disassembly')}</option>
                    <option value="both">{t('admin.both')}</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#94A3B8]">{t('projects.startDate')}</Label>
                  <Input
                    type="date"
                    value={projectData.start_date}
                    onChange={(e) => setProjectData({ ...projectData, start_date: e.target.value })}
                    className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#94A3B8]">{t('projects.endDate')}</Label>
                  <Input
                    type="date"
                    value={projectData.end_date}
                    onChange={(e) => setProjectData({ ...projectData, end_date: e.target.value })}
                    className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                  />
                </div>
              </div>

              {/* Requirements */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#94A3B8]">{t('admin.crewSize')}</Label>
                  <Input
                    type="number"
                    min="1"
                    value={projectData.required_crew_size}
                    onChange={(e) => setProjectData({ ...projectData, required_crew_size: e.target.value })}
                    className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#94A3B8]">{t('admin.complexity')}</Label>
                  <select
                    value={projectData.complexity}
                    onChange={(e) => setProjectData({ ...projectData, complexity: e.target.value })}
                    className="w-full bg-black/50 border border-white/10 text-white px-4 py-2 rounded-lg focus:border-[#23F7DD]"
                  >
                    <option value="low">{t('admin.low')}</option>
                    <option value="medium">{t('admin.medium')}</option>
                    <option value="high">{t('admin.high')}</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#94A3B8]">{t('admin.estHours')}</Label>
                  <Input
                    type="number"
                    min="1"
                    value={projectData.estimated_duration_hours}
                    onChange={(e) => setProjectData({ ...projectData, estimated_duration_hours: e.target.value })}
                    placeholder="40"
                    className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                  />
                </div>
              </div>

              {/* Budget */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#94A3B8]">{t('admin.budgetMin')}</Label>
                  <Input
                    type="number"
                    min="0"
                    value={projectData.budget_min}
                    onChange={(e) => setProjectData({ ...projectData, budget_min: e.target.value })}
                    placeholder="30000"
                    className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#94A3B8]">{t('admin.budgetMax')}</Label>
                  <Input
                    type="number"
                    min="0"
                    value={projectData.budget_max}
                    onChange={(e) => setProjectData({ ...projectData, budget_max: e.target.value })}
                    placeholder="50000"
                    className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                  />
                </div>
              </div>

              {/* Safety Notes */}
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">{t('admin.safetyNotes')}</Label>
                <Textarea
                  value={projectData.safety_notes}
                  onChange={(e) => setProjectData({ ...projectData, safety_notes: e.target.value })}
                  placeholder={t('admin.safetyRequirements')}
                  rows={2}
                  className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                />
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setProjectDialogOpen(false)}
                  className="border-white/20 text-[#94A3B8]"
                >
                  {t('common.cancel')}
                </Button>
                <Button 
                  type="submit" 
                  disabled={saving}
                  className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
                >
                  {saving ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin"></span>
                      {t('admin.saving')}
                    </span>
                  ) : (
                    editingProject ? t('admin.updateProject') : t('projects.createProject')
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
